/**
 * +detail: broadleaf trees and bushes for the open zone (meadow, dells, farm, lakeside, town), in
 * the painted-anime style: crowns are clusters of soft rounded masses (smooth normals, so the toon
 * ramp turns each into a clean light / shade shape) with a warm sunlit top, a cool teal underside
 * and small puffs on the silhouette; trunks are tapered and a little bent, with boughs reaching
 * into the crown. The dark teal conifers (outdoor/valley.ts) stay the forest and highland identity.
 *
 * Ids start with `tree_` / `bush_` so the field builder merges them into the outline-free, swaying
 * nature chunks (outdoor/field.ts NATURE_ID / SWAY_ID).
 */
import * as THREE from 'three';
import { type Gen, combine, paint, registerOutdoorProcs, xf } from '../procs';
import { type RGB3, SUN, clamp01, cyl, displaceShared, limb, mix, puff, rgb, scale3, tint } from './kit';

/** A canopy palette (sRGB hex): shade, body, lit, sun-catching tips. */
interface Leaf {
  deep: number;
  mid: number;
  light: number;
  tip: number;
}

const LEAF = {
  meadow: { deep: 0x1f5a48, mid: 0x3f8f3c, light: 0x7fc04a, tip: 0xc8e070 },
  birch: { deep: 0x2f6a44, mid: 0x5fa548, light: 0x9fd060, tip: 0xe0ec88 },
  linden: { deep: 0x1d5444, mid: 0x3a8440, light: 0x6fb04a, tip: 0xb4d468 },
  willow: { deep: 0x2a6646, mid: 0x5c9e46, light: 0x96c85a, tip: 0xd0e47c },
  apple: { deep: 0x235e44, mid: 0x4a9440, light: 0x86c04e, tip: 0xc4dc6c },
  poplar: { deep: 0x1c4e40, mid: 0x37803e, light: 0x66a848, tip: 0xa8cc60 },
  dark: { deep: 0x123c38, mid: 0x245e3a, light: 0x3f7f44, tip: 0x6c9e50 },
  blossom: { deep: 0xa85a7a, mid: 0xe08aaa, light: 0xf6b8cc, tip: 0xffe2ec },
  autumn: { deep: 0x7a3a2a, mid: 0xc8642c, light: 0xeaa040, tip: 0xffd878 },
} as const satisfies Record<string, Leaf>;

const BARK = 0x6b4a36;
const BARK_DARK = 0x45302a;

/** Canopy colour at a crown-local point: cool teal under, body, warm lit top, sun-side tips. */
function leafShade(pal: Leaf, y0: number, y1: number): (x: number, y: number, z: number, nx: number, ny: number, nz: number) => RGB3 {
  const deep = rgb(pal.deep);
  const mid = rgb(pal.mid);
  const light = rgb(pal.light);
  const tip = rgb(pal.tip);
  return (_x, y, _z, nx, ny, nz) => {
    const t = clamp01((y - y0) / Math.max(0.01, y1 - y0));
    const up = ny * 0.5 + 0.5;
    const sun = clamp01(nx * SUN.x + ny * SUN.y + nz * SUN.z);
    let c = mix(deep, mid, clamp01(t * 0.75 + up * 0.55 - 0.2));
    c = mix(c, light, clamp01(up * 1.3 - 0.6) * (0.45 + 0.55 * t));
    c = mix(c, tip, sun * sun * 0.4 * (0.3 + 0.7 * t));
    // Underside: cool and dark (sky bounce stays teal, never grey).
    if (ny < -0.25) c = mix(c, scale3(deep, 0.9), clamp01((-ny - 0.25) * 1.6) * 0.6);
    return c;
  };
}

interface CrownOpts {
  y: number;
  rx: number;
  ry: number;
  /** Outer masses. */
  n: number;
  /** Mass radius range (yd). */
  r: readonly [number, number];
  pal: Leaf;
  /** Silhouette puffs per upper mass (0..2). */
  puffs?: number;
  ws?: number;
  hs?: number;
  /** Offset of the whole crown (lean). */
  dx?: number;
  dz?: number;
}

/** A clustered crown: a core mass and `n` masses spread over an ellipsoid (denser on top), puffs on the upper ones. */
function crown(rnd: () => number, o: CrownOpts, parts: THREE.BufferGeometry[]): { y0: number; y1: number; pts: RGB3[] } {
  const ws = o.ws ?? 8;
  const hs = o.hs ?? 6;
  const dx = o.dx ?? 0;
  const dz = o.dz ?? 0;
  const y0 = o.y - o.ry * 1.05;
  const y1 = o.y + o.ry * 1.25;
  const shade0 = leafShade(o.pal, y0, y1);
  // Each mass gets its own value / hue nudge (painted clusters, not one flat green).
  const shadeK = (k: number, g: number) => (x: number, y: number, z: number, nx: number, ny: number, nz: number): RGB3 => {
    const c = shade0(x, y, z, nx, ny, nz);
    return [c[0] * k, c[1] * k * g, c[2] * k];
  };
  const shade = shadeK(1, 1);
  const pts: RGB3[] = [];
  const core = puff(Math.max(o.rx, o.ry) * 0.74, rnd, 0.12, ws, hs, 0.65);
  core.scale(o.rx / Math.max(o.rx, o.ry), o.ry / Math.max(o.rx, o.ry), o.rx / Math.max(o.rx, o.ry));
  parts.push(tint(core.translate(dx, o.y, dz), shade));
  for (let i = 0; i < o.n; i++) {
    // Golden-angle spiral over the upper ~80% of the ellipsoid.
    const phi = Math.acos(1 - (1.62 * (i + 0.5)) / o.n);
    const th = i * 2.39996 + rnd() * 0.7;
    const sx = Math.sin(phi) * Math.cos(th);
    const sy = Math.cos(phi);
    const sz = Math.sin(phi) * Math.sin(th);
    const r = (o.r[0] + rnd() * (o.r[1] - o.r[0])) * (1 - 0.22 * (phi / Math.PI));
    const k = 0.7 + rnd() * 0.12;
    const p: RGB3 = [dx + sx * o.rx * k, o.y + sy * o.ry * k, dz + sz * o.rx * k];
    pts.push(p);
    const sh = shadeK(0.9 + rnd() * 0.2, 0.97 + rnd() * 0.06);
    parts.push(tint(puff(r, rnd, 0.2, ws, hs, 0.72).translate(p[0], p[1], p[2]), sh));
    const np = sy > 0.15 ? (o.puffs ?? 1) : 0;
    for (let j = 0; j < np; j++) {
      const a = th + (j ? 1.4 : -0.5) + rnd() * 0.6;
      const q: RGB3 = [p[0] + Math.cos(a) * r * 0.66 + sx * r * 0.3, p[1] + r * (0.42 + rnd() * 0.2), p[2] + Math.sin(a) * r * 0.66 + sz * r * 0.3];
      parts.push(tint(puff(r * (0.36 + rnd() * 0.12), rnd, 0.12, 6, 4, 0.5).translate(q[0], q[1], q[2]), sh));
    }
  }
  return { y0, y1, pts };
}

/** A tapered, slightly bent trunk with a root flare (bark darker at the foot). */
function trunk(rnd: () => number, h: number, r0: number, r1: number, bend: number, color = BARK): THREE.BufferGeometry[] {
  const g = new THREE.CylinderGeometry(r1, r0, h, 7, 3).translate(0, h / 2, 0).toNonIndexed();
  const ph = rnd() * 6;
  displaceShared(g, (v) => {
    const t = v.y / h;
    v.x += Math.sin(t * 2.2 + ph) * bend * t;
    v.z += Math.cos(t * 1.7 + ph) * bend * 0.6 * t;
  });
  g.computeVertexNormals();
  const flare = new THREE.ConeGeometry(r0 * 1.9, r0 * 2.4, 7).translate(0, r0 * 1.2, 0);
  return [paint(g, color, [0.55, 1.0]), paint(flare, BARK_DARK, [0.6, 0.9])];
}

/** Boughs from the trunk top into a few of the crown's masses. */
function boughs(rnd: () => number, from: number, pts: readonly RGB3[], n: number, r: number): THREE.BufferGeometry[] {
  const out: THREE.BufferGeometry[] = [];
  const pick = pts.filter((p) => p[1] < pts.reduce((m, q) => Math.max(m, q[1]), 0) - 0.1);
  for (let i = 0; i < Math.min(n, pick.length); i++) {
    const p = pick[Math.floor(rnd() * pick.length)]!;
    out.push(paint(limb([0, from, 0], [p[0] * 0.7, p[1] - 0.4, p[2] * 0.7], r, r * 0.45), BARK, [0.75, 1.0]));
  }
  return out;
}

/** The meadow oak: a short sturdy trunk and a broad, clustered dome. */
function oakRound(height: number, pal: Leaf, n = 9): Gen {
  return (rnd) => {
    const H = height * (0.9 + rnd() * 0.2);
    const parts: THREE.BufferGeometry[] = trunk(rnd, H * 0.5, H * 0.06, H * 0.035, H * 0.04);
    const c = crown(rnd, { y: H * 0.64, rx: H * 0.37, ry: H * 0.27, n, r: [H * 0.12, H * 0.18], pal, puffs: 1 }, parts);
    parts.push(...boughs(rnd, H * 0.42, c.pts, 3, H * 0.03));
    return combine(parts);
  };
}

/** Birch: a slender pale trunk with dark marks, small airy clusters up the stem. */
const birch = (height: number): Gen => (rnd) => {
  const H = height * (0.9 + rnd() * 0.2);
  const g = new THREE.CylinderGeometry(0.1, 0.2, H * 0.85, 6, 4).translate(0, H * 0.425, 0).toNonIndexed();
  const bend = (rnd() - 0.5) * 0.5;
  displaceShared(g, (v) => {
    v.x += Math.sin((v.y / H) * 2.4) * bend;
  });
  g.computeVertexNormals();
  const white = rgb(0xe8e2d4);
  const mark = rgb(0x3a3430);
  const ph = rnd() * 10;
  const parts: THREE.BufferGeometry[] = [tint(g, (x, y, z) => (Math.sin(y * 5.3 + ph + Math.atan2(z, x) * 1.5) > 0.8 || y < 0.35 ? mark : scale3(white, 0.8 + 0.25 * clamp01(y / H))), true)];
  const clusters = 3 + Math.floor(rnd() * 2);
  for (let i = 0; i < clusters; i++) {
    const t = i / (clusters - 1);
    const a = rnd() * Math.PI * 2;
    const off = (1 - t) * H * 0.12;
    crown(rnd, { y: H * (0.5 + t * 0.4), rx: H * (0.17 - t * 0.05), ry: H * (0.11 - t * 0.02), n: 3, r: [H * 0.07, H * 0.1], pal: LEAF.birch, puffs: t > 0.6 ? 1 : 0, ws: 7, hs: 5, dx: Math.cos(a) * off + Math.sin(t * 2.4) * bend, dz: Math.sin(a) * off }, parts);
  }
  return combine(parts);
};

/** Linden: a tall oval crown on a straight trunk (roadside, meadow). */
function linden(height: number, pal: Leaf = LEAF.linden): Gen {
  return (rnd) => {
    const H = height * (0.9 + rnd() * 0.2);
    const parts: THREE.BufferGeometry[] = trunk(rnd, H * 0.46, H * 0.045, H * 0.025, H * 0.02);
    const c = crown(rnd, { y: H * 0.62, rx: H * 0.22, ry: H * 0.32, n: 10, r: [H * 0.1, H * 0.14], pal, puffs: 1 }, parts);
    parts.push(...boughs(rnd, H * 0.38, c.pts, 2, H * 0.022));
    return combine(parts);
  };
}

/** Poplar: a narrow column (farm windbreaks). */
const poplar = (height: number): Gen => (rnd) => {
  const H = height * (0.9 + rnd() * 0.2);
  const parts: THREE.BufferGeometry[] = trunk(rnd, H * 0.3, H * 0.035, H * 0.022, H * 0.01);
  crown(rnd, { y: H * 0.57, rx: H * 0.11, ry: H * 0.4, n: 9, r: [H * 0.06, H * 0.09], pal: LEAF.poplar, puffs: 0, ws: 7, hs: 5 }, parts);
  return combine(parts);
};

/** Willow (lakeside): a leaning trunk, an umbrella crown and curtains of hanging fronds. */
const willow = (height: number): Gen => (rnd) => {
  const H = height * (0.9 + rnd() * 0.2);
  const lean = (rnd() - 0.5) * 1.6;
  const parts: THREE.BufferGeometry[] = trunk(rnd, H * 0.62, H * 0.07, H * 0.04, lean);
  const c = crown(rnd, { y: H * 0.72, rx: H * 0.42, ry: H * 0.2, n: 9, r: [H * 0.13, H * 0.17], pal: LEAF.willow, puffs: 1, dx: lean * 0.6 }, parts);
  parts.push(...boughs(rnd, H * 0.5, c.pts, 3, H * 0.03));
  const shade = leafShade(LEAF.willow, c.y0 - H * 0.4, c.y1);
  const n = 22;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + rnd() * 0.25;
    const rr = H * 0.42 * (0.75 + rnd() * 0.3);
    const L = H * (0.26 + rnd() * 0.18);
    const top = H * 0.66 + (rnd() - 0.5) * H * 0.05;
    const f = new THREE.ConeGeometry(H * 0.045, L, 4).rotateX(Math.PI).translate(lean * 0.6 + Math.cos(a) * rr, top - L / 2, Math.sin(a) * rr);
    parts.push(tint(f, shade));
  }
  return combine(parts);
};

/** Orchard apple: a short trunk, a round crown with red fruit. */
const apple = (height: number): Gen => (rnd) => {
  const H = height * (0.9 + rnd() * 0.2);
  const parts: THREE.BufferGeometry[] = trunk(rnd, H * 0.42, H * 0.06, H * 0.035, H * 0.05);
  const c = crown(rnd, { y: H * 0.64, rx: H * 0.4, ry: H * 0.28, n: 7, r: [H * 0.16, H * 0.22], pal: LEAF.apple, puffs: 1, ws: 7, hs: 5 }, parts);
  parts.push(...boughs(rnd, H * 0.36, c.pts, 2, H * 0.035));
  for (let i = 0; i < 9; i++) {
    const p = c.pts[i % c.pts.length]!;
    const a = rnd() * Math.PI * 2;
    parts.push(paint(xf(new THREE.IcosahedronGeometry(H * 0.03, 0), [p[0] + Math.cos(a) * H * 0.14, p[1] - H * 0.04, p[2] + Math.sin(a) * H * 0.14]), i % 3 ? 0xd8402e : 0xf0a030, [1, 1.15]));
  }
  return combine(parts);
};

// ---------------------------------------------------------------------------------------------
// Bushes and tall grass
// ---------------------------------------------------------------------------------------------

/** A round shrub: a few soft masses in a low dome (walk-through dressing). */
function bushRound(r0: number, pal: Leaf, flowers: readonly number[] = []): Gen {
  return (rnd) => {
    const parts: THREE.BufferGeometry[] = [];
    const shade = leafShade(pal, 0, r0 * 1.9);
    const n = 4 + Math.floor(rnd() * 2);
    const pts: RGB3[] = [];
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + rnd() * 0.5;
      const r = r0 * (0.5 + rnd() * 0.2);
      const p: RGB3 = [Math.cos(a) * r0 * 0.55, r * 0.75, Math.sin(a) * r0 * 0.55];
      pts.push(p);
      parts.push(tint(puff(r, rnd, 0.16, 8, 6, 0.6).translate(...p), shade));
    }
    parts.push(tint(puff(r0 * 0.68, rnd, 0.14, 7, 5, 0.45).translate(0, r0 * 0.95, 0), shade));
    for (let i = 0; i < flowers.length * 4; i++) {
      const a = rnd() * Math.PI * 2;
      const up = 0.3 + rnd() * 0.6;
      const rr = r0 * (0.85 - up * 0.4);
      parts.push(paint(xf(new THREE.OctahedronGeometry(0.08 + rnd() * 0.03, 0), [Math.cos(a) * rr, r0 * (0.5 + up * 0.85), Math.sin(a) * rr]), flowers[i % flowers.length]!, [1.05, 1.15]));
    }
    return combine(parts);
  };
}

/** A clump of long grass blades (meadow verges; sways with the trees). */
const grassClump: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const n = 12 + Math.floor(rnd() * 6);
  const root = rgb(0x3f7a34);
  const tipC = rgb(0xb8d468);
  for (let i = 0; i < n; i++) {
    const a = rnd() * Math.PI * 2;
    const d = Math.sqrt(rnd()) * 0.35;
    const h = 0.7 + rnd() * 0.6;
    const lean = 0.2 + rnd() * 0.35;
    const w = 0.05 + rnd() * 0.03;
    const bx = Math.cos(a) * d;
    const bz = Math.sin(a) * d;
    const tx = bx + Math.cos(a) * lean;
    const tz = bz + Math.sin(a) * lean;
    const px = -Math.sin(a) * w;
    const pz = Math.cos(a) * w;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([bx - px, 0, bz - pz, bx + px, 0, bz + pz, tx, h, tz, bx + px, 0, bz + pz, bx - px, 0, bz - pz, tx, h, tz], 3));
    // Up-facing normals: blades shade like the ground (no dark backfaces).
    g.setAttribute('normal', new THREE.Float32BufferAttribute([0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0], 3));
    parts.push(tint(g, (_x, y) => mix(root, tipC, clamp01(y / h))));
  }
  return combine(parts);
};

// ---------------------------------------------------------------------------------------------
// The Hollowcrown: the giant ancient oak on its knoll north-west of town (the zone's landmark)
// ---------------------------------------------------------------------------------------------

/**
 * ~40 yd tall: a massive twisted trunk with deep bark grooves, roots arching over the knoll, six
 * great limbs and a broad, layered crown of big soft masses (the field builds it as its own
 * always-drawn mesh so it reads from across the map).
 */
const elderTree: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const TH = 14;
  const bark = rgb(0x6e4c38);
  const barkDark = rgb(0x3e2a24);
  const mossC = rgb(0x557f3a);
  const g = new THREE.CylinderGeometry(1.7, 3.6, TH, 14, 7).translate(0, TH / 2, 0).toNonIndexed();
  displaceShared(g, (v) => {
    const t = v.y / TH;
    const a = Math.atan2(v.z, v.x) + t * 1.1;
    const rr = Math.hypot(v.x, v.z) * (1 + 0.12 * Math.sin(a * 5 + t * 3) + 0.06 * Math.sin(a * 11));
    v.set(Math.cos(a) * rr + Math.sin(t * 2.6) * 0.9 * t, v.y, Math.sin(a) * rr + Math.cos(t * 1.9) * 0.5 * t);
  });
  g.computeVertexNormals();
  parts.push(
    tint(g, (x, y, z, _nx, ny) => {
      const groove = Math.sin(Math.atan2(z, x) * 9 + y * 0.35) > 0.55 ? 0.72 : 1;
      const c = mix(barkDark, bark, clamp01(0.35 + y / TH));
      return ny > 0.5 && y < 6 ? mossC : scale3(c, groove);
    }),
  );
  // Roots arching out over the knoll.
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2 + rnd() * 0.4;
    const L = 6.5 + rnd() * 3;
    const pts = [new THREE.Vector3(Math.cos(a) * 2.2, 3.2, Math.sin(a) * 2.2), new THREE.Vector3(Math.cos(a) * (3.4 + L * 0.25), 1.8 + rnd() * 0.6, Math.sin(a) * (3.4 + L * 0.25)), new THREE.Vector3(Math.cos(a + 0.15) * (2.8 + L * 0.7), 0.5, Math.sin(a + 0.15) * (2.8 + L * 0.7)), new THREE.Vector3(Math.cos(a + 0.2) * (2.6 + L), -0.4, Math.sin(a + 0.2) * (2.6 + L))];
    const tube = new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 8, 0.75 - i * 0.02, 5, false);
    const tp = tube.attributes['position'] as THREE.BufferAttribute;
    // Taper toward the tip.
    for (let k = 0; k < tp.count; k++) {
      const vx = tp.getX(k);
      const vz = tp.getZ(k);
      const d = Math.hypot(vx, vz);
      const f = 1 - clamp01((d - 3) / (L + 1)) * 0.6;
      const cx = Math.cos(a) * d;
      const cz = Math.sin(a) * d;
      tp.setXYZ(k, cx + (vx - cx) * f, tp.getY(k), cz + (vz - cz) * f);
    }
    tube.computeVertexNormals();
    parts.push(tint(tube, (_x, y, _z, _nx, ny) => (ny > 0.6 ? mossC : mix(barkDark, bark, clamp01(y / 3)))));
  }
  parts.push(paint(new THREE.ConeGeometry(5.2, 5, 12).translate(0, 2.5, 0), 0x4e3628, [0.6, 0.95]));
  // Great limbs.
  const limbs: RGB3[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + rnd() * 0.5;
    const from: RGB3 = [Math.sin(1.6) * 0.9, TH - 2.5 + rnd() * 2, 0];
    const to: RGB3 = [Math.cos(a) * (10 + rnd() * 4), TH + 5 + rnd() * 3, Math.sin(a) * (10 + rnd() * 4)];
    limbs.push(to);
    parts.push(paint(limb(from, to, 1.3, 0.55, 7), 0x5e4030, [0.7, 1.0]));
    parts.push(paint(limb(to, [to[0] * 1.25, to[1] + 3.5, to[2] * 1.25], 0.55, 0.25, 5), 0x5e4030, [0.8, 1.0]));
  }
  // The crown: a broad low dome of big masses, then a second, smaller tier on top.
  const pal: Leaf = { deep: 0x22604a, mid: 0x4a9a44, light: 0x8cc858, tip: 0xdcea84 };
  crown(rnd, { y: TH + 8.5, rx: 20, ry: 7.5, n: 28, r: [4.2, 6.0], pal, puffs: 2, ws: 10, hs: 7 }, parts);
  crown(rnd, { y: TH + 14.5, rx: 11, ry: 5.5, n: 11, r: [3.6, 4.8], pal, puffs: 2, ws: 9, hs: 6, dx: 1.2 }, parts);
  for (const l of limbs) parts.push(tint(puff(4.2, rnd, 0.15, 9, 6, 0.5).translate(l[0] * 1.15, l[1] + 1.2, l[2] * 1.15), leafShade(pal, TH + 1, TH + 22)));
  // Hanging moss / vines under the crown.
  const vine = leafShade({ deep: 0x2a5a3e, mid: 0x4a7e3a, light: 0x6f9a48, tip: 0x9cba5c }, TH - 4, TH + 8);
  for (let i = 0; i < 26; i++) {
    const a = rnd() * Math.PI * 2;
    const d = 6 + rnd() * 10;
    const L = 2 + rnd() * 3.5;
    parts.push(tint(new THREE.ConeGeometry(0.22, L, 3).rotateX(Math.PI).translate(Math.cos(a) * d, TH + 4.5 - L / 2 - rnd(), Math.sin(a) * d), vine));
  }
  return combine(parts);
};

/**
 * Low-poly broadleaf tree for the instanced far ring (~60 triangles): three faceted crown masses,
 * lit top / teal underside, a short trunk. Height ~7 yd at scale 1, like the far spruce.
 */
export function farBroadleafGeometry(leaf: number): THREE.BufferGeometry {
  const c = rgb(leaf);
  const under = mix(c, rgb(0x1f4a44), 0.45);
  const top = mix(c, rgb(0xb8d468), 0.3);
  const crownAt = (r: number, at: RGB3): THREE.BufferGeometry =>
    tint(new THREE.IcosahedronGeometry(r, 0).scale(1, 0.82, 1).translate(...at), (_x, y, _z, _nx, ny) => mix(mix(under, c, clamp01((y - at[1] + r) / (r * 1.4))), top, clamp01(ny) * 0.5), true);
  return combine([paint(cyl(0.22, 0.34, 3.2, 5), BARK, [0.55, 0.95]), crownAt(2.3, [0, 4.6, 0]), crownAt(1.6, [1.2, 5.6, 0.4]), crownAt(1.5, [-0.9, 5.9, -0.6])]);
}

registerOutdoorProcs({
  tree_oak_round: oakRound(9, LEAF.meadow, 12),
  tree_oak_wide: oakRound(11.5, LEAF.meadow, 14),
  tree_oak_dark: oakRound(10, LEAF.dark, 12),
  tree_blossom_round: oakRound(7, LEAF.blossom, 10),
  tree_autumn: oakRound(8, LEAF.autumn, 10),
  tree_birch: birch(9),
  tree_linden: linden(11),
  tree_poplar: poplar(12),
  tree_willow: willow(8.5),
  tree_apple: apple(5.6),
  bush_round: bushRound(0.75, LEAF.meadow),
  bush_flowering: bushRound(0.7, LEAF.apple, [0xffffff, 0xf6c0d4, 0xfff0a0]),
  bush_berry: bushRound(0.65, LEAF.linden, [0xd0384a, 0x8a2a5a]),
  bush_dense: bushRound(1.0, LEAF.linden),
  bush_grass: grassClump,
  eldertree: elderTree,
});
